import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { Mag } from '../components/mag.js';
import { magToast } from '../components/toaster.js';
import {
  finalizeGoogleConnect,
  type GoogleConnectHandoffCustomer,
  readGoogleConnectHandoff,
} from '../lib/api.js';
import { env } from '../lib/env.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery } from '../lib/queries.js';

// /brand/$slug/google-connect/picker — landing page after Google
// redirects the founder back from OAuth (s199). The handoff query
// param is a short-lived token the api stashed at callback time;
// we read it, render the user's accessible google ads customer ids,
// and finalize on click.

const searchSchema = z.object({
  handoff: z.string().min(1).max(200),
});

export const Route = createFileRoute('/brand/$slug/google-connect/picker')({
  validateSearch: (s) => searchSchema.parse(s),
  component: GoogleConnectPickerPage,
});

function GoogleConnectPickerPage() {
  const navigate = useNavigate();
  const { handoff } = Route.useSearch();
  const { slug } = Route.useParams();
  const [finalizingCustomerId, setFinalizingCustomerId] = useState<string | null>(null);

  const handoffQuery = useQuery({
    queryKey: ['google-connect-handoff', handoff],
    queryFn: () => readGoogleConnectHandoff(handoff),
    retry: false,
  });
  const brandsQ = useQuery(brandsQuery());
  const brandFromSlug = brandsQ.data?.find((b) => b.slug === slug) ?? null;

  const retryUrl = brandFromSlug
    ? `${env.API_URL}/api/brands/${brandFromSlug.id}/google/connect/start`
    : null;

  const onPick = async (customer: GoogleConnectHandoffCustomer) => {
    if (!handoffQuery.data) return;
    setFinalizingCustomerId(customer.id);
    try {
      await finalizeGoogleConnect(handoffQuery.data.brandId, handoff, customer.id);
      magToast.success(
        i18n._(msg`connected.`),
        i18n._(
          msg`mag can read this google ads account now. she'll surface insights on the next scan.`,
        ),
      );
      // Honor the returnTo from the handoff when present — it carries
      // the surface that triggered the OAuth (e.g. /campaigns/import).
      // The server validated it before signing it into the state
      // token, so navigating to it is safe even though the raw
      // string came from a query param. Falls back to settings when
      // missing (founder hit the picker from the integrations page
      // or a legacy entry point).
      const returnTo = handoffQuery.data.returnTo;
      if (returnTo) {
        window.location.assign(returnTo);
        return;
      }
      await navigate({
        to: '/brand/$slug/settings/integrations',
        params: { slug },
      });
    } catch (err) {
      magToast.error("couldn't finalize.", founderErrorCopy(err));
      setFinalizingCustomerId(null);
    }
  };

  if (handoffQuery.isLoading) {
    return (
      <PickerShell backToBrandSlug={slug}>
        <p>
          <Trans>loading…</Trans>
        </p>
      </PickerShell>
    );
  }
  if (handoffQuery.isError || !handoffQuery.data) {
    return (
      <PickerShell backToBrandSlug={slug}>
        <article className="rounded-2xl border-[1.5px] border-[var(--plum)]/40 bg-[var(--spell-soft)]/40 p-5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
            {'↘'} <Trans>this link has expired</Trans>
          </p>
          <p className="mt-2 font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
            <Trans>
              “the connect handoff isn't valid anymore — either it expired (5 min cap) or someone
              already used it. no harm done, just start over.”
            </Trans>
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <Link to="/brand/$slug/channels" params={{ slug }} className="link-quiet text-[12px]">
              {'↘'} <Trans>open channels</Trans>
            </Link>
            {retryUrl ? (
              <a href={retryUrl} className="btn">
                <Trans>try connecting again</Trans>
                <span className="btn-arrow">→</span>
              </a>
            ) : null}
          </div>
        </article>
      </PickerShell>
    );
  }

  const customers = handoffQuery.data.customers;

  if (customers.length === 0) {
    return (
      <PickerShell backToBrandSlug={slug}>
        <p className="font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
          <Trans>
            “you don't have any google ads accounts attached to this google login. create one at
            ads.google.com, link it to this email, then come back.”
          </Trans>
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <a
            href="https://ads.google.com/aw/overview"
            target="_blank"
            rel="noreferrer"
            className="link-quiet text-[12px]"
          >
            <Trans>open google ads</Trans>
          </a>
          {retryUrl ? (
            <a href={retryUrl} className="btn">
              <Trans>try connecting again</Trans>
              <span className="btn-arrow">→</span>
            </a>
          ) : null}
        </div>
      </PickerShell>
    );
  }

  return (
    <PickerShell backToBrandSlug={slug}>
      <p className="font-serif text-[16px] leading-[1.5] text-[var(--ink)]/85">
        <Trans>
          “pick the google ads account mag should read for this brand. you can change this later in
          settings.”
        </Trans>
      </p>
      <ul className="mt-6 flex flex-col gap-3">
        {customers.map((customer) => (
          <CustomerRow
            key={customer.id}
            customer={customer}
            // [task c6a530c] Which brand already holds this account, on a
            // reuse handoff. Undefined on the OAuth path.
            takenBy={handoffQuery.data?.takenByBrand?.[customer.id]}
            onPick={() => void onPick(customer)}
            finalizing={finalizingCustomerId === customer.id}
            anyFinalizing={finalizingCustomerId !== null}
          />
        ))}
      </ul>
      {customers.length === 1 ? (
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
          {'↘'} <Trans>only one account on this google login — quick pick.</Trans>
        </p>
      ) : null}
    </PickerShell>
  );
}

/** Per-customer row. Same chrome as the meta-connect picker's
 *  PageRow — bordered card, plum drop shadow, ink-on-spell pill on
 *  the right. Manager (MCC) rows render disabled with a "pick a
 *  sub-account" hint, and an inline `manager · mcc` tag inside the
 *  sub mono line so the founder can see at a glance why they can't
 *  click. */
function CustomerRow({
  customer,
  takenBy,
  onPick,
  finalizing,
  anyFinalizing,
}: {
  customer: GoogleConnectHandoffCustomer;
  /** The brand already using this account, when one is. Reuse only. */
  takenBy?: string | undefined;
  onPick: () => void;
  finalizing: boolean;
  anyFinalizing: boolean;
}) {
  const label = customer.descriptiveName ?? `Account ${formatCustomerId(customer.id)}`;
  const isManager = customer.isManager === true;
  // [spec `one-ad-account-several-brands`] A taken account is now PICKABLE.
  // It used to be disabled for the same reason a manager row is — picking it
  // could not do what the founder expected, because (org, provider, customer)
  // was unique and finalizing would have MOVED the account off `takenBy`,
  // leaving that brand disconnected with no notice. Migration 0232 put
  // brand_id in the key, so picking it gives this brand its own row and the
  // sibling keeps theirs.
  //
  // The chip stays: "on gestionalo" is still a true and useful fact about the
  // account, and a founder deliberately sharing one wants to see which others
  // are on it. It describes, it no longer forbids.
  const isShared = takenBy !== undefined;
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        disabled={anyFinalizing || isManager}
        title={
          isManager
            ? "manager accounts can't run ads themselves — pick a sub-account instead"
            : isShared
              ? `also connected on ${takenBy} — picking it here shares the account, it does not move it`
              : undefined
        }
        className="group flex w-full items-center justify-between gap-4 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 text-left shadow-[3px_3px_0_var(--plum)] transition-all duration-150 enabled:hover:-translate-x-0.5 enabled:hover:-translate-y-0.5 enabled:hover:shadow-[5px_5px_0_var(--plum)] disabled:cursor-not-allowed disabled:opacity-60 sm:p-5"
      >
        <div className="min-w-0">
          <p className="truncate font-display text-[17px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
            {label}
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 truncate font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
            <span>
              {'↘'} <Trans>customer id · {formatCustomerId(customer.id)}</Trans>
            </span>
            {isManager ? (
              <span className="inline-flex items-center rounded-full border border-[var(--ink)]/30 bg-[var(--paper)] px-2 py-0.5 text-[9.5px] font-semibold tracking-[0.08em] text-[var(--ink)]/65">
                <Trans>manager · mcc</Trans>
              </span>
            ) : null}
            {isShared ? (
              <span className="inline-flex items-center rounded-full border border-[var(--ink)]/30 bg-[var(--paper)] px-2 py-0.5 text-[9.5px] font-semibold tracking-[0.08em] text-[var(--ink)]/65">
                <Trans>also on {takenBy}</Trans>
              </span>
            ) : null}
          </p>
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 font-display text-[13px] font-bold text-[var(--spell)]">
          {finalizing ? (
            <Trans>connecting…</Trans>
          ) : isManager ? (
            <Trans>unavailable</Trans>
          ) : isShared ? (
            // Still "share this", not "taken": the account is pickable, and
            // the verb is the one honest difference from an unused account —
            // this brand joins it rather than claiming it.
            <Trans>share this</Trans>
          ) : (
            <Trans>pick this</Trans>
          )}
          <span aria-hidden className="mag-arrow mag-arrow-e">
            →
          </span>
        </span>
      </button>
    </li>
  );
}

function formatCustomerId(raw: string): string {
  if (raw.length !== 10) return raw;
  return `${raw.slice(0, 3)}-${raw.slice(3, 6)}-${raw.slice(6)}`;
}

function PickerShell({
  children,
  backToBrandSlug,
}: {
  children: React.ReactNode;
  backToBrandSlug?: string | undefined;
}) {
  return (
    <main className="min-h-screen bg-[var(--cream)]">
      {/* Sticky header strip matches the brief/onboarding wizards and
          the meta-connect picker: magig wordmark on the left, step
          indicator + exit on the right. Founders land here mid-OAuth
          from Google — the wordmark grounds them back inside the
          magig app. */}
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b border-[var(--line)] bg-[var(--paper)] px-4 sm:px-7">
        <Link to="/home" className="flex items-center gap-2 no-underline" aria-label="magig">
          <span className="relative font-display text-[18px] font-black tracking-[-0.04em] text-[var(--ink)]">
            magig
            <span className="absolute -top-0.5 left-[52%] text-[6px] leading-none text-[var(--spell)]">
              ✦
            </span>
          </span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="inline-flex items-center rounded-full bg-[var(--spell)] px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink)]">
            <Trans>step 2 · pick account</Trans>
          </span>
          {backToBrandSlug ? (
            <Link
              to="/brand/$slug/settings/integrations"
              params={{ slug: backToBrandSlug }}
              className="btn btn-ghost btn-sm"
            >
              <Trans>exit</Trans>
            </Link>
          ) : null}
        </div>
      </header>

      <section className="mx-auto max-w-[760px] px-4 pb-20 pt-12 sm:px-7">
        <div className="flex items-start gap-4">
          <Mag size={64} mood="smug" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              {'↘'} <Trans>connect google ads</Trans>
            </p>
            <h1 className="mt-2 font-display text-[30px] font-black leading-[1.05] tracking-[-0.04em] sm:text-[40px]">
              <Trans>pick the right account.</Trans>
            </h1>
            <div className="mt-5">{children}</div>
          </div>
        </div>
      </section>
    </main>
  );
}
